import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { addError, fetchTerms, initPayment, markStepAsLoaded, openProcessingSpinner, removeError, saveCustomerNotes } from '../../../redux/actions';
import { store } from '../../../redux/store';
import { Unsubscribe } from '@stencil/redux';
import { Cart, OrderKind, PaymentConfig, PaymentMethod, RtrError, SplitOption, StepItem, System, Term } from '../../../redux/types';
import { RootState } from '../../../redux/reducers';
import { AmountToPay, ComponentView, Price, StepErrorType } from '../../../types/types';
import { PaymentStepState } from './utils';
import { validatePayment } from '../../rtr-payment/utils/validate';

@Component({
  tag: 'rtr-step-payment',
  shadow: true,
  styleUrl: 'style.sass',
})
export class RtrStepPayment {
  @Prop({ mutable: true }) isRecurringMode: boolean;
  @Prop({ mutable: true }) currentMethod: PaymentMethod;
  @Prop({ mutable: true }) secondaryMethod: PaymentMethod;
  @Prop({ mutable: true }) cart: Cart;
  @Prop({ mutable: true }) terms: Term[];
  @Prop({ mutable: true }) system: System;
  @Prop({ mutable: true }) paymentConfig: PaymentConfig;
  @Prop({ mutable: true }) paymentSplitOption: SplitOption;
  @Prop({ mutable: true }) amountToPay: AmountToPay;
  @Prop({ mutable: true }) customer: any;
  @Prop({ mutable: true }) billingStepValues: any;
  @Prop({ mutable: true }) orderKind: OrderKind;
  @Prop({ mutable: true }) currency: string;
  @Prop({ mutable: true }) currencyPosition: string;

  @Prop() currentStep: StepItem;
  @Prop() orderReference: string;
  @Prop() errors: RtrError[];
  @Prop() isWaiting: boolean = false;
  @Prop() setCheckoutWaiting: (value: boolean) => void;
  @Prop() onActionButton: () => void;
  @Prop() removeStepError;

  @State() customerMessage: string;
  @State() termsValid: boolean = false;
  @State() methodValid: boolean = true;

  @Event() rtrPaymentOptionsNotValidated: EventEmitter;
  @Event() rtrPaymentMethodsNotValidated: EventEmitter;

  saveCustomerNotes!: typeof saveCustomerNotes;
  fetchTerms!: typeof fetchTerms;
  initPayment!: typeof initPayment;
  unsubscribe!: Unsubscribe;
  markStepAsLoaded!: typeof markStepAsLoaded;
  addError!: typeof addError;
  openProcessingSpinner!: typeof openProcessingSpinner;
  removeError!: typeof removeError;

  termsFormRef: any;
  termsRefs = [];

  @Event() rtrStartPaymentEvent: EventEmitter;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        cart: state.cart,
        terms: state.terms,
        system: state.system,
        paymentConfig: state.payment,
        isRecurringMode: state.app.recurringPaymentMode,
        currentMethod: state.app.paymentMethod,
        secondaryMethod: state.app.secondaryPaymentMethod,
        paymentSplitOption: state.app.paymentSplitOption,
        amountToPay: state.app.amountToPay,
        customer: state.app.customer,
        billingStepValues: state.app.billingStepValues,
        orderKind: state.bookingDetails.orderDetails?.kind,
        currency: state.system.currency,
        currencyPosition: state.system.pricing.format.currencyPosition,
      };
    });

    store.mapDispatchToProps(this, {
      saveCustomerNotes,
      fetchTerms,
      markStepAsLoaded,
      initPayment,
      addError,
      openProcessingSpinner,
      removeError,
    });
  }

  componentDidLoad() {
    this.fetchTerms();
    this.initPayment({ orderReference: this.orderReference });
    this.markStepAsLoaded(this.currentStep.id, this.currentStep.visitContext);
  }

  componentDidUpdate() {
    if (!this.cart) {
      return null;
    }
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  handleVoucherSuccess = () => {
    this.removeStepError(this.currentStep.id, StepErrorType.PaymentNoOptionSelected);
  };

  handleActionButton = async () => {
    // validate payment
    const paymentOptionsValidated: boolean = await validatePayment({
      method: this.currentMethod?.value,
      component: ComponentView.Checkout,
      giftCard: this.paymentConfig.giftCard,
      secondaryMethod: this.secondaryMethod?.value,
      amountToPay: this.calculatedAmountToPay,
    });

    if (!paymentOptionsValidated) {
      this.rtrPaymentOptionsNotValidated.emit();
    } else {
      // validate selected method
      if (!this.methodValid && this.calculatedAmountToPay > 0) {
        this.rtrPaymentMethodsNotValidated.emit();
      } else {
        // validate terms
        await this.termsFormRef.submitForm();
        const termsValid = await this.termsFormRef.isFormValid();
        if (termsValid) {
          this.openProcessingSpinner();
          this.rtrStartPaymentEvent.emit({
            onPaymentSuccess: () => {
              this.onActionButton();
            },
          });
        } else {
          setTimeout(async () => {
            await this.termsFormRef.submitForm();

            const firstTermWithError = this.termsRefs.find(ref => {
              const classes = [...ref.classList];
              return classes.includes('ContainerError');
            });

            firstTermWithError?.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }, 0);
        }
      }
    }
  };

  get stepState(): PaymentStepState {
    const paymentValidated: boolean = validatePayment({
      method: this.currentMethod?.value,
      component: ComponentView.Checkout,
      giftCard: this.paymentConfig.giftCard,
      secondaryMethod: this.secondaryMethod?.value,
      amountToPay: this.calculatedAmountToPay,
    });

    if (!paymentValidated) return PaymentStepState.SelectPayment;

    if (!this.methodValid && this.calculatedAmountToPay > 0) return PaymentStepState.ProvideMethod;

    if (!this.termsValid) return PaymentStepState.AcceptTerms;

    return PaymentStepState.Ready;
  }

  handleFormChange = async () => {
    this.termsValid = await this.termsFormRef.isFormValid();
  };

  handleMethodValidated = async (validated: boolean) => {
    this.methodValid = validated;
  };

  get totalPriceWithoutDeposit() {
    return this.cart?.discountedPriceWithoutDeposit;
  }

  get depositPrice(): Price {
    return this.cart?.depositPrice;
  }

  get priceWithSplit() {
    return this.amountToPay ? { gross: this.amountToPay.total, net: this.amountToPay.total, vat: 0 } : { gross: 0, net: 0, vat: 0 };
  }

  get calculatedAmountToPay(): number {
    const baseAmount = this.amountToPay.total;
    const payingFullPrice = this.amountToPay?.total === this.amountToPay?.withSubtractedGiftCard;

    if (!payingFullPrice) {
      return this.amountToPay?.withSubtractedGiftCard;
    }

    return baseAmount;
  }

  render() {
    if (!this.paymentConfig || !this.cart) {
      return null;
    }

    return (
      <Fragment>
        {this.isWaiting && <rentware-spinner withOverlay={true} fixedStyle={true} />}

        <rtr-step-payment-template
          isRecurringMode={this.isRecurringMode}
          customerMessage={this.customerMessage}
          currentMethod={this.currentMethod}
          secondaryMethod={this.secondaryMethod}
          showVoucher={this.paymentConfig.hasVouchers}
          terms={this.terms}
          saveCustomerNotes={this.saveCustomerNotes}
          system={this.system}
          errors={this.errors}
          currentStep={this.currentStep}
          termsRefs={el => {
            if (!this.termsRefs.includes(el)) {
              this.termsRefs.push(el);
            }
          }}
          termsFormRef={el => (this.termsFormRef = el)}
          totalPrice={this.totalPriceWithoutDeposit}
          depositPrice={this.depositPrice}
          priceWithSplit={this.priceWithSplit}
          onVoucherSuccess={this.handleVoucherSuccess}
          orderReference={this.orderReference}
          cart={this.cart}
          customer={this.customer}
          setCheckoutWaiting={this.setCheckoutWaiting}
          billingStepValues={this.billingStepValues}
          currency={this.currency}
          currencyPosition={this.currencyPosition}
          calculatedAmountToPay={this.calculatedAmountToPay}
          orderKind={this.orderKind}
          stepState={this.stepState}
          onActionButton={this.handleActionButton}
          onFormChange={this.handleFormChange}
          setMethodValidated={this.handleMethodValidated}
        />
      </Fragment>
    );
  }
}
